import { Grid } from '@mui/material'
import { isLastItem, SortChangeEvent, SortDirection } from '@mprolab/sdk'
import styled from 'styled-components'
import _ from 'lodash'
import React, { useRef, type HTMLAttributes } from 'react'
import { HeightProps, height } from 'styled-system'
import { getHeader, getRows } from './Table.logic'
import { TableColumn, AlignOptions, EmptyRowCell, EmptyTableRow } from './Table.types'
import { Text, TextProps, TextVariant } from '../Text'
import PaginationButtons from './components/PaginationButtons'
import { MediaQuery } from '../../theme/base'
import LoadingRows from './components/LoadingRows'
import { Flex } from '../Flex'

const ContentCard = styled(Flex)<{ plain: boolean }>`
  flex-direction: column;
  padding: ${({ plain }) => (plain ? '0' : '24px 32px')};
  width: 100%;
  background: ${({ theme, plain }) => (plain ? 'unset' : theme.colors.backgroundContrast)};
`

const StyledTableTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: 16px;
  font-family: 'BiennaleBold';
`

const StyledHeaderTitle = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))<{ align?: AlignOptions }>`
  color: ${({ theme }) => theme.colors.white3};
  display: flex;
  justify-content: ${({ align }) => align || 'flex-start'};
  text-align: ${({ align }) => (align === 'flex-end' ? 'end' : 'initial')};
  align-items: center;
`

const DefaultCellText = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))<{ align?: AlignOptions }>`
  display: flex;
  justify-content: ${({ align }) => align || 'flex-start'};
  align-items: center;
  color: ${({ theme }) => theme.colors.white};
  padding-right: 8px;
`

const StyledHeaderWrapper = styled(Grid).withConfig({
  shouldForwardProp: (prop) => !['fullViewMediaquery', 'fullViewCustomWidth'].includes(String(prop)),
})<{ fullViewMediaquery?: MediaQuery; fullViewCustomWidth: number }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray7};
  padding-bottom: 8px;
  ${({ fullViewMediaquery, fullViewCustomWidth, theme }) =>
    fullViewMediaquery &&
    `
    ${theme.mediaQueries[fullViewMediaquery]} {
      min-width: ${fullViewCustomWidth}px;
    }
`}
`

const StyledRowWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['isLastItem', 'clickable', 'fullViewMediaquery', 'fullViewCustomWidth'].includes(String(prop)),
})<
  {
    isLastItem?: boolean
    clickable?: boolean
    fullViewMediaquery?: MediaQuery
    fullViewCustomWidth: number
  } & HTMLAttributes<HTMLDivElement>
>`
  position: relative;
  padding: 8px 0px;
  ${({ isLastItem, theme }) =>
    !isLastItem &&
    `
    border-bottom: 1px solid ${theme.colors.gray7};
  `};

  ${({ clickable }) =>
    clickable &&
    ` 
    cursor: pointer;
  `}
  ${({ fullViewMediaquery, fullViewCustomWidth, theme }) =>
    fullViewMediaquery &&
    `
  ${theme.mediaQueries[fullViewMediaquery]} {
    min-width: ${fullViewCustomWidth}px;
  }
`}
`

const LoadingDataBox = styled.div<HeightProps>`
  @keyframes loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  min-height: 20px;
  height: 100%;
  color: transparent;
  background: ${({ theme }) =>
    `linear-gradient(100deg, ${theme.colors.gray7} 30%, ${theme.colors.gray8} 50%, ${theme.colors.gray7} 70%)`};
  animation: loading 2s ease-in-out infinite;
  background-size: 500%;
  margin: 0px 4px;
  border-radius: 15px;
  ${height}
`

/**
 * A height-capped area for the ROWS, leaving the header in place above it.
 *
 * A table that simply grows pushes everything below it down the page — a
 * holder with fifteen licences got a column of rows taller than the panel
 * beside it, which is what this replaces. Capping it keeps the page still and
 * moves the length into a scroll the reader controls.
 *
 * The scrollbar is deliberately visible here, unlike the horizontal one
 * above: a hidden scrollbar on a clipped list is a list that looks truncated,
 * with no sign there is more.
 */
const RowsScrollArea = styled.div<{ maxBodyHeight?: string }>`
  width: 100%;
  ${({ maxBodyHeight }) =>
    maxBodyHeight &&
    `
    max-height: ${maxBodyHeight};
    overflow-y: auto;
    overflow-x: hidden;

    ::-webkit-scrollbar {
      display: block;
      width: 6px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.18);
    }
    ::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
    scrollbar-width: thin;
  `}
`

const ListScrollableContainer = styled.div.withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'fullViewMediaquery',
})<
  { fullViewMediaquery?: MediaQuery } & HTMLAttributes<HTMLDivElement>
>`
  width: 100%;

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  ${({ fullViewMediaquery, theme }) =>
    fullViewMediaquery &&
    `
  ${theme.mediaQueries[fullViewMediaquery]} {
    overflow-x: auto;
    overflow-y: hidden;
  }
`}
`

type CellTextComponent = React.ElementType<TextProps & { align?: AlignOptions }>

const cellComponent = (
  cellValue: string | React.ReactNode,
  align: AlignOptions,
  onSingleCellClick?: (value: string | number | boolean) => void,
  CustomText?: CellTextComponent
) => {
  const handleCellClick = (value: string | number | boolean | undefined) => {
    if (value && onSingleCellClick) onSingleCellClick(value)
  }

  if (typeof cellValue === 'object') return cellValue

  return CustomText ? (
    <CustomText
      align={align}
      onClick={() => {
        handleCellClick(cellValue)
      }}
    >
      {cellValue}
    </CustomText>
  ) : (
    <DefaultCellText
      align={align}
      onClick={() => {
        handleCellClick(cellValue)
      }}
    >
      {cellValue}
    </DefaultCellText>
  )
}

export const onHeaderCellClick = (
  columnKey: string,
  sortDirection?: SortDirection,
  onSortChange?: (e: SortChangeEvent) => void
) => {
  if (onSortChange && sortDirection === SortDirection.ASCENDING) {
    onSortChange({ sortDirection: SortDirection.DESCENDING, key: columnKey })
  } else if (onSortChange && (!sortDirection || sortDirection === SortDirection.DESCENDING)) {
    onSortChange({ sortDirection: SortDirection.ASCENDING, key: columnKey })
  }
}

export interface TableProps {
  tableTitle?: string
  data: any[]
  columns: TableColumn[]
  onRowClick?: (row: any) => void
  onCellClick?: (value: string | number | boolean) => void
  onSortChange?: (e: SortChangeEvent) => void
  rowsPerPage?: number
  dataCount?: number
  onPageChanged?: (pageChangeEvent: { pageNumber: number; pageSize: number; skipNumber: number }) => void
  loading?: boolean
  fetching?: boolean
  error?: boolean
  fullViewMediaquery?: MediaQuery
  fullViewCustomWidth?: number
  emptyListComponent?: React.ReactNode
  plain?: boolean
  /**
   * Cap the rows at this height and scroll the rest, instead of letting the
   * table grow down the page. A CSS length; the header stays outside it.
   */
  maxBodyHeight?: string
}

const TableSquare: React.FunctionComponent<TableProps> = (props) => {
  const {
    tableTitle,
    data = [],
    columns,
    onRowClick,
    onCellClick,
    onSortChange,
    rowsPerPage = 5,
    dataCount,
    onPageChanged,
    loading = true,
    fetching = false,
    fullViewMediaquery = MediaQuery.mobile,
    fullViewCustomWidth = 1000,
    emptyListComponent,
    plain = false,
    maxBodyHeight
  } = props

  const tableRows = getRows(data, columns)
  const header = getHeader(columns)
  const tableCellDataRef = useRef<HTMLDivElement>(null)

  const emptyCellsArray: EmptyRowCell[] = columns.map((column: TableColumn) => {
    return { gridColumns: column.gridColumns || 2 }
  })
  const emptyRowsArray: EmptyTableRow[] = Array(rowsPerPage)
    .fill(null)
    .map(() => {
      return { cells: emptyCellsArray }
    })

  const getRowsContent = () => {
    switch (true) {
      case loading:
        return <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} />
      case !_.isEmpty(tableRows):
        return tableRows.map((tableRow, index) => {
          const { cells } = tableRow
          return (
            <StyledRowWrapper
              id={`${tableRow.key}_${index}_tableRow`}
              key={`${tableRow.key}_${index}`}
              isLastItem={isLastItem(tableRows, index)}
              onClick={() => {
                if (onRowClick && tableRow && tableRow.cells) {
                  let rowObj = {}
                  tableRow.cells.forEach((c) => {
                    rowObj = { ...rowObj, [c.columnKey]: c.value }
                  })
                  onRowClick(rowObj)
                }
              }}
              clickable={!_.isNil(onRowClick)}
              fullViewMediaquery={fullViewMediaquery}
              fullViewCustomWidth={fullViewCustomWidth}
            >
              <Grid container columnSpacing={1}>
                {cells.map((cell, index) => {
                  const { gridColumns, value, hide, align } = cell
                  return (
                    <Grid
                      ref={tableCellDataRef}
                      id={`${value}_${index}_tableCell`}
                      key={`${tableRow.key}_${cell.columnKey}_${index}`}
                      item
                      xs={gridColumns || 'auto'}
                      display={hide ? 'none' : 'block'}
                    >
                      {loading || fetching ? (
                        <LoadingDataBox height={tableCellDataRef.current?.clientHeight || '24px'} />
                      ) : (
                        cellComponent(value, align, onCellClick)
                      )}
                    </Grid>
                  )
                })}
              </Grid>
            </StyledRowWrapper>
          )
        })
      case _.isEmpty(tableRows):
        return (
          <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} isEmpty>
            {emptyListComponent}
          </LoadingRows>
        )
      default:
        return <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} />
    }
  }

  return (
    <Flex flexDirection="column" fullWidth>
      <ContentCard {...{ plain }}>
        {tableTitle && <StyledTableTitle variant={TextVariant.BODY_BIG_BOLD}>{tableTitle}</StyledTableTitle>}
        <ListScrollableContainer fullViewMediaquery={fullViewMediaquery}>
          <StyledHeaderWrapper
            container
            fullViewMediaquery={fullViewMediaquery}
            fullViewCustomWidth={fullViewCustomWidth}
          >
            {!_.isNil(header) ? (
              header?.cells.map((headerCell, index) => {
                const { align, gridColumns, value, columnKey, hide, sortDirection } = headerCell
                return (
                  <Grid
                    id={`${headerCell.value}_${index}_headerCell`}
                    key={columnKey}
                    item
                    xs={gridColumns}
                    onClick={() => onHeaderCellClick(columnKey, sortDirection, onSortChange)}
                    display={hide ? 'none' : 'block'}
                  >
                    {loading ? <LoadingDataBox /> : cellComponent(value, align, onCellClick, StyledHeaderTitle)}
                  </Grid>
                )
              })
            ) : (
              <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} />
            )}
          </StyledHeaderWrapper>
          <RowsScrollArea maxBodyHeight={maxBodyHeight}>{getRowsContent()}</RowsScrollArea>
        </ListScrollableContainer>
      </ContentCard>
      {rowsPerPage && dataCount && dataCount / rowsPerPage > 1 ? (
        <PaginationButtons
          maxPage={Math.ceil(dataCount / rowsPerPage)}
          onPageChanged={(pageNumber) => {
            if (onPageChanged)
              onPageChanged({ pageNumber, pageSize: rowsPerPage, skipNumber: (pageNumber - 1) * rowsPerPage })
          }}
        />
      ) : (
        <></>
      )}
    </Flex>
  )
}

export default TableSquare
